import { BusinessModelEnum } from '@/entities/user/models/BusinessModel'
import { columnGroupingModel } from '@/fragments/PlanningInboxTable/libs/useInboxColumnGroupingModel'
import { CollapsibleHeader } from '@/shared/components/GridTable/CollapsibleHeader/CollapsibleHeader'
import { useAppSelector } from '@/shared/store'
import { GridColumnGroupingModel, GridColumnVisibilityModel } from '@mui/x-data-grid-premium'

export const depotColumnGroupingModel: GridColumnGroupingModel = [
  {
    groupId: 'orderDetails',
    description: 'Details about the order',
    headerName: 'Order details',
    renderHeaderGroup: (params) => {
      return <CollapsibleHeader
        groupFields={[
          'order.orderNumber', 'order.weight', 'order.volume', 'order.quantity', 'order.timeWindow',
          'order.serviceTime', 'order.priority', 'order.maxOnboardTime', 'order.portalClientName', 'order.displayOrderAttributes',
          'order.tripNumber', 'order.consignmentNumber', 'order.description',
          'order.driverNote', 'order.deliveryInstruction', 'order.comment',
        ]}
        numColumnsWhenCollapsed={4}
        {...params}
      />
    },
    children: [
      { field: 'order.orderNumber' },
      { field: 'order.weight' },
      { field: 'order.volume' },
      { field: 'order.quantity' },
      { field: 'order.timeWindow' },
      { field: 'order.serviceTime' },
      { field: 'order.priority' },
      { field: 'order.maxOnboardTime' },
      { field: 'order.portalClientName' },
      { field: 'order.displayOrderAttributes' },
      { field: 'order.tripNumber' },
      { field: 'order.consignmentNumber' },
      { field: 'order.description' },
      { field: 'order.driverNote' },
      { field: 'order.deliveryInstruction' },
      { field: 'order.comment' },
    ],
  },
  {
    groupId: 'assignmentDetails',
    description: 'Assignment details',
    headerName: 'Assignment Details',
    renderHeaderGroup: (params) => {
      return <CollapsibleHeader
        groupFields={['assignedTo', 'newLocation']}
        numColumnsWhenCollapsed={2}
        {...params}
      />
    },
    children: [
      { field: 'assignedTo' },
      { field: 'newLocation' },
    ],
  },
  {
    groupId: 'addressDetails',
    description: 'Address details',
    headerName: 'Address',
    renderHeaderGroup: (params) => {
      return <CollapsibleHeader
        groupFields={[
          'order.location.displayAddress', 
          'order.location.suburb',
          'order.location.state',
          'order.location.postcode',
          'order.location.mapRef',
          'order.location.additional',
        ]}
        numColumnsWhenCollapsed={1}
        {...params}
      />
    },
    children: [
      { field: 'order.location.displayAddress' },
      { field: 'order.location.suburb' },
      { field: 'order.location.state' },
      { field: 'order.location.postcode' },
      { field: 'order.location.mapRef' },
      { field: 'order.location.additional' },
    ],
  },
  {
    groupId: 'customerDetails',
    description: 'Customer details',
    headerName: 'Customer',
    renderHeaderGroup: (params) => {
      return <CollapsibleHeader
        groupFields={['order.location.externalId', 'order.location.name']}
        numColumnsWhenCollapsed={1}
        {...params}
      />
    },
    children: [
      { field: 'order.location.externalId' },
      { field: 'order.location.name' },
    ],
  },
  {
    groupId: 'depotDetails',
    description: 'Depot details',
    headerName: 'Depot',
    renderHeaderGroup: (params) => {
      return <CollapsibleHeader
        groupFields={['order.depot.externalId', 'order.depot.name']}
        numColumnsWhenCollapsed={1}
        {...params}
      />
    },
    children: [
      { field: 'order.depot.externalId' },
      { field: 'order.depot.name' },
    ],
  },
]

export type ColumnGroupingModelResults = {
  columnGroupingModel: GridColumnGroupingModel
  initialColumnGroupVisibility: GridColumnVisibilityModel
  initialColumnVisibility?: GridColumnVisibilityModel
}

export const useColumnGroupingModel = (): ColumnGroupingModelResults => {
  const businessModel = useAppSelector((state) => state.user.businessModel)

  if (businessModel === BusinessModelEnum.Depot) {
    return {
      columnGroupingModel: depotColumnGroupingModel,
      initialColumnGroupVisibility: {
        orderDetails: false,
        assignmentDetails: true,
        addressDetails: true,
        customerDetails: true,
        depotDetails: true,
      },
    }
  }

  const initialColumnGroupVisibility: GridColumnVisibilityModel = {
    orderDetails: false,
    assignmentDetails: true,
    addressDetails: true,
    customerDetails: true,
    depotDetails: true,
  }

  return {
    columnGroupingModel,
    initialColumnGroupVisibility,
  }
}